import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, useParams } from 'react-router-dom';
import { Container } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import { useSnackbar } from 'notistack';
import moment from 'moment-timezone';
import { makeStyles } from '@material-ui/core/styles';
import { TextField } from '@material-ui/core';

import Summary from './Summary';
import InternalIdentifier from './InternalIdentifier';
import ReservationTime from './ReservationTime';
import CarpoolingPassenger from './CarpoolingPassenger';
import Power from './Power';

import StationLocation from '../../../components/vehicles/VehicleDetails/StationLocation';
import { useErrorToaster } from '../../../utils/errorToaster';
import closeIcon from '../../../assets/images/close.svg';
import api from '../../../utils/api';
import { useVehicleLoader } from '../../../hooks/vehicleLoader';
import AskPrivacyUsage from '../../bookings/AskPrivacyUsage';
import { useStationLoader } from '../../../hooks/stationLoader';
import Photo from '../../vehicles/VehicleDetails/Photo';
import CarpoolingInfo from '../../rentals/carpoolings/CarpoolingInfo';
import CancelBookingModal from '../../bookings/CancelBookingModal';
import ModifyBookingModal from '../../bookings/ModifyBookingModal';
import AskModal from '../../bookings/AskModal';
import { useModal } from '../../../utils/modal';
import { rentalDateLongFormat } from '../../../utils/variables';
import {
  updatePrivacyUsage,
  deletePassenger
} from '../../../store/actions/rentals';

import config from '../../../config';
import _ from 'lodash';
import {
  inProgressStatuses,
  closedStatuses,
  upcomingStatuses,
  runningStatuses,
  preparedStatuses
} from './../../../utils/rentalStatuses';
import { duration } from '../../../utils/dateHelper';
import { distance } from '../../../utils/distanceHelper';
import {
  initCancellationMinimalDelay,
  initBoundsModificationMinimalDelay,
  initModificationReservationEndTimeMinimalDelay,
  initModificationReservationStartTimeMinimalDelay
} from '../../../store/selectors/tenant';
import Price from '../../shared/Price';
import ElectricOptions from '../VehicleDetails/ElectricOptions';
import Equipments from '../VehicleDetails/Equipments';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '30px;',
    width: '100%',
    marginBottom: '20px'
  }
}));

const VehicleBody = ({ history }) => {
  const { t } = useTranslation(['booking', 'common', 'search']);
  const displayError = useErrorToaster();
  const dispatch = useDispatch();
  const tenant = useSelector(state => state.tenant);
  const userId = useSelector(state => state.account.id);

  const useEstimatedDistance = tenant.useEstimatedDistance;
  const useEstimatedDestinationField = tenant.useEstimatedDestinationField;

  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [isLoaded, setIsLoaded] = useState(false);
  const [rental, setRental] = useState(null);
  const [driver, setDriver] = useState(null);
  const [geotracking, setGeotracking] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const { vehicle } = useVehicleLoader(rental ? rental.vehicle_id : null);
  const startStation = useStationLoader(
    rental ? rental.start_station_id : null
  );
  const endStation = useStationLoader(rental ? rental.end_station_id : null);
  const language = useSelector(state => state.app.language);
  const cancellationMinimalDelay = useSelector(initCancellationMinimalDelay);
  const modificationReservationEndTimeMinimalDelay = useSelector(
    initModificationReservationEndTimeMinimalDelay
  );
  const modificationReservationStartTimeMinimalDelay = useSelector(
    initModificationReservationStartTimeMinimalDelay
  );
  const modifyModal = useModal();
  const askCancelModal = useModal();
  const cancelModal = useModal();
  const modalTracking = useModal();

  useEffect(() => {
    const loadData = async () => {
      const { rental } = await api.rentals.getOne(id);
      const { user } = await api.user.getProfile(rental.driver_id);
      const { passengers } =
        tenant.useCarpooling && rental.carpooling
          ? await api.rentals.getPassengers(rental.id)
          : { passengers: [] };
      setDriver(user);
      setRental(rental);
      setPassengers(passengers);
      setGeotracking(rental.geotracking);
      setIsLoaded(true);
    };

    loadData();
  }, [id]);

  const handleClose = () => {
    const path = window.location.pathname;
    let page = 'bookings';
    if (path.includes('/account/carpoolings')) {
      page = 'carpoolings';
    } else if (path.includes('/account/bookings-history')) {
      page = 'bookings-history';
    }
    history.push(`${config.auth.rootUrl}account/${page}`);
  };

  const handleChangeSuccess = () => {
    enqueueSnackbar(t('booking:modification-confirmed'), {
      variant: 'success'
    });
    handleClose();
  };

  const handleCancelParticipation = async () => {
    try {
      let response = await dispatch(
        deletePassenger({
          rentalId: rental.id,
          passengerId: userId
        })
      );

      enqueueSnackbar(t('booking:carpooling-updated-confirmed'), {
        variant: 'success'
      });
      history.push(`${config.auth.rootUrl}account/carpoolings`);
    } catch (error) {
      displayError(error);
    }
  };

  const handleCancelSuccess = () => {
    enqueueSnackbar(t('booking:cancellation-confirmed'), {
      variant: 'success'
    });
    handleClose();
  };

  const isCancellable = () => {
    if (closedStatuses.includes(rental.status)) return false;
    if (rental.driver_id !== userId) return false;
    return (
      moment(rental.reservation_start_time) > cancellationMinimalDelay &&
      upcomingStatuses.includes(rental.status)
    );
  };

  const geotrackingUsageWithStatus = () => {
    return ['in_progress'].includes(rental.status);
  };

  const canRefusePrivacyUsage = () => {
    if (rental.driver_id !== userId) return false;
    if (
      geotrackingUsageWithStatus() &&
      tenant.usePrivacyUsageTimeSlot &&
      vehicle.geotracking_privacy_usage &&
      geotracking
    )
      return true;
    return false;
  };

  const canActiveGeotracking = () => {
    if (rental.driver_id !== userId) return false;
    if (
      geotrackingUsageWithStatus() &&
      tenant.usePrivacyUsageTimeSlot &&
      vehicle.geotracking_privacy_usage &&
      !geotracking
    )
      return true;
    return false;
  };

  const canCancelCarpoolingParticipation = () => {
    return (
      rental &&
      rental.driver_id !== userId &&
      upcomingStatuses.includes(rental.status)
    );
  };

  const isEditable = () => {
    if (rental.driver_id !== userId) return false;
    if (closedStatuses.includes(rental.status)) return false;
    if (tenant.reservationStartTimeModificationMinimalDelay === -1)
      return false;
    if (tenant.reservationEndTimeModificationMinimalDelay === -1) return false;
    if (tenant.reservationStartTimeModificationMinimalDelay === 0) return true;
    if (tenant.reservationEndTimeModificationMinimalDelay === 0) return true;
    return (
      (isEditableReservationStartTime() &&
        upcomingStatuses.includes(rental.status)) ||
      (isEditableReservationEndTime() &&
        runningStatuses.includes(rental.status))
    );
  };

  const isEditableReservationStartTime = () => {
    return (
      moment(rental.reservation_start_time) >
      modificationReservationStartTimeMinimalDelay
    );
  };

  const isEditableReservationEndTime = () => {
    return (
      moment(rental.reservation_end_time) >
      modificationReservationEndTimeMinimalDelay
    );
  };

  const handleCloseGeotracking = async close => {
    return;
  };

  const handleGeotracking = async () => {
    setGeotracking(!geotracking);
    handlePrivacyUsage(!geotracking);
  };

  const handleChoisePassenger = async newPassenger => {
    if (!newPassenger) return;
    try {
      const { passenger } = await api.rentals.createNewPassenger(
        rental.id,
        newPassenger.email
      );
      const { passengers } = await api.rentals.getPassengers(rental.id);

      enqueueSnackbar(t('booking:carpooling-updated-confirmed'), {
        variant: 'success'
      });
      setPassengers(passengers);
    } catch (error) {
      displayError(error);
    }
  };

  const handlePrivacyUsage = async currentGeotracking => {
    try {
      let responseRental = await dispatch(
        updatePrivacyUsage({
          id: rental.id,
          geotracking: currentGeotracking
        })
      );

      enqueueSnackbar(t('booking:geotracking-deleted-confirmed'), {
        variant: 'success'
      });
      setRental(responseRental);
    } catch (error) {
      displayError(error);
    }
  };

  const rentalDuration = () => {
    return duration(
      rental.reservation_start_time,
      rental.reservation_end_time,
      language
    );
  };

  const showVehicleLocation = () => {
    return (
      preparedStatuses.includes(rental.status) &&
      vehicle?.box_latitude &&
      vehicle?.box_longitude
    );
  };

  const handleCancelClick = () => {
    const title = t('booking:cancel-participation-title');
    const message = t('booking:cancel-participation-message');

    if (window.confirm(`${title}\n\n${message}`)) {
      modifyModal.open();
    }
  };

  const driverCanAddPassenger = () => {
    const addPassengerAllowedStatuses = ['PENDING', 'PREPARED', 'CHECK_IN'];

    const canAdd =
      rental != null &&
      userId != null &&
      tenant?.useCarpooling === true &&
      tenant?.carpoolingDriverCanAddPassengers === true &&
      rental.carpooling === true &&
      rental.driver_id === userId &&
      addPassengerAllowedStatuses.includes(rental.status);

    return canAdd;
  };

  const renderStationInfo = () => {
    if (closedStatuses.includes(rental.status)) return null;
    if (inProgressStatuses.includes(rental.status)) return null;

    const start = startStation?.station;
    const end = endStation?.station;

    if (showVehicleLocation()) {
      return (
        <div className="section flex-row">
          <div>
            <div className="title">{t('booking:vehicle-location')}</div>
          </div>
        </div>
      );
    }

    return (
      <div className={start ? 'section flex-row' : 'hidden'}>
        <div>
          <div className="title">{t('booking:station-start-location')}</div>
          {start?.name && <div className="category">{start.name}</div>}
          {start?.address && (
            <div className="section-content">{start.address}</div>
          )}
        </div>
        {end && end.name !== start?.name && (
          <div>
            <div className="title">{t('booking:station-end-location')}</div>
            <div className="category">{end.name}</div>
            <div className="section-content">
              {end.address ? end.address : null}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRentalMapLocation = () => {
    if (closedStatuses.includes(rental.status)) return null;
    if (inProgressStatuses.includes(rental.status)) return null;

    let latitude = showVehicleLocation()
      ? vehicle.box_latitude
      : startStation?.station?.latitude;
    let longitude = showVehicleLocation()
      ? vehicle.box_longitude
      : startStation?.station?.longitude;

    return (
      <div className="section">
        {latitude && longitude && (
          <StationLocation latitude={latitude} longitude={longitude} />
        )}
      </div>
    );
  };

  return (
    <div className="vehicle-booking booking-details">
      <Container maxWidth="md" className="account-page-container">
        <Card className={classes.root}>
          {isLoaded ? (
            <div className="page-header">
              {rental && rental.driver_id !== userId ? (
                <Typography component="h1">
                  {t('booking:your-carpooling')}
                </Typography>
              ) : (
                <Typography component="h1">
                  {t('booking:your-booking')}
                </Typography>
              )}
              <div className="close-button">
                <IconButton onClick={handleClose}>
                  <img src={closeIcon} alt="" width="30" height="30" />
                </IconButton>
              </div>
            </div>
          ) : null}
          {isLoaded && vehicle && startStation && rental ? (
            <div>
              <Photo image={vehicle.photos ? vehicle.photos[0] : null} />

              <div className="sections">
                <Summary vehicle={vehicle} />

                <InternalIdentifier vehicle={vehicle} />

                <ReservationTime rental={rental} />

                {tenant.useCarpooling && rental.carpooling ? (
                  <div>
                    <div className="section flex-row">
                      <div>
                        <CarpoolingInfo driver={driver} rental={rental} />
                      </div>
                      <div>
                        <CarpoolingPassenger
                          userId={userId}
                          tenant={tenant}
                          rental={rental}
                          currentPassengers={passengers}
                          handleChoisePassenger={handleChoisePassenger}
                          driverCanAddPassengers={driverCanAddPassenger}
                        />
                      </div>
                    </div>

                    {rental && rental.carpooling_destination && (
                      <div className="section">
                        <div className="title">
                          {t('booking:destination-carpooling')}
                        </div>
                        <div className="section-content">
                          {rental.carpooling_destination}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {vehicle.fuel_type === 'ELECTRIC' &&
                upcomingStatuses.indexOf(rental.status) > -1 ? (
                  <div className="section vehicle-summary">
                    <div className="title">{t('booking:vehicle-soc')}</div>
                    <div className="section-content">
                      <ElectricOptions
                        unitAutonomy={tenant.distanceUnit}
                        stateOfCharge={vehicle.box_state_of_charge}
                        chargingStatus={vehicle.box_charging_status}
                        autonomy={vehicle.box_autonomy}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="hidden section">
                  <div className="title">{t('booking:included-distance')}</div>
                  <div className="section-content">
                    XXX Km/{t('common:day')}
                  </div>
                </div>

                <div className="section flex-row">
                  <div>
                    <div className="title">{t('booking:duration')}</div>
                    <div className="section-content">{rentalDuration()}</div>
                  </div>
                  {rental.estimated_distance ? (
                    <div>
                      <div className="title">
                        {t('booking:estimated-distance')}
                      </div>
                      <div className="section-content">
                        {`${rental.estimated_distance} ${rental.distance_unit}`}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div
                  className={
                    rental.distance &&
                    closedStatuses.indexOf(rental.status) > -1
                      ? 'section'
                      : 'hidden'
                  }
                >
                  <div className="title">{t('booking:distance')}</div>
                  <div className="section-content">
                    {rental.distance} {rental.distance_unit}
                  </div>
                </div>

                {rental.price && rental.price.value_cents && (
                  <div
                    className={
                      closedStatuses.indexOf(rental.status) > -1
                        ? 'section'
                        : 'hidden'
                    }
                  >
                    <div className="title">{t('booking:price')}</div>
                    <div className="section-content">
                      <Price
                        cents={rental.price.value_cents}
                        currency={rental.price.iso_code}
                      />
                    </div>
                  </div>
                )}

                <div
                  className={
                    Array.isArray(vehicle.options) && vehicle.options.length > 0
                      ? 'section'
                      : 'hidden'
                  }
                >
                  <div className="title">{t('booking:equipments')}</div>
                  <div className="section-content">
                    <Equipments equipments={vehicle.options} />
                  </div>
                </div>

                <Power vehicle={vehicle} />

                {renderStationInfo()}
                {renderRentalMapLocation()}

                {useEstimatedDistance &&
                useEstimatedDestinationField &&
                rental &&
                rental.destination ? (
                  <div className="section">
                    <div className="title">{t('search:destination')}</div>
                    <div className="section-content">
                      {rental.destination.formatted_address}
                    </div>
                    <div className="section-content">
                      {rental?.destination.label}
                    </div>
                  </div>
                ) : null}

                <div className={vehicle.description ? 'section' : 'hidden'}>
                  <div className="title">{t('booking:description')}</div>
                  <div className="section-content">{vehicle.description}</div>
                </div>
              </div>

              {canRefusePrivacyUsage() ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6}>
                    <Button
                      className={'booking-actions'}
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={modalTracking.open}
                    >
                      {t('booking:refuse-tracking')}
                    </Button>
                  </Grid>
                </Grid>
              ) : null}

              {canActiveGeotracking() ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6}>
                    <Button
                      className={'booking-actions'}
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={modalTracking.open}
                    >
                      {t('booking:active-tracking')}
                    </Button>
                  </Grid>
                </Grid>
              ) : null}

              <Grid container spacing={2}>
                {canCancelCarpoolingParticipation() && (
                  <Grid item xs={12} sm={12} md={6}>
                    <Button
                      className={'booking-actions'}
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={askCancelModal.open}
                    >
                      {t('booking:cancel-participation-title')}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={12} md={6}>
                  <Button
                    className={isEditable() ? 'booking-actions' : 'hidden'}
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={modifyModal.open}
                  >
                    {t('booking:modify-booking')}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <Button
                    className={
                      isCancellable() ? 'cancel-reservation-button' : 'hidden'
                    }
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={cancelModal.open}
                  >
                    {t('booking:cancel-booking')}
                  </Button>
                </Grid>
              </Grid>
            </div>
          ) : null}
        </Card>
      </Container>

      {isLoaded ? (
        <div>
          <AskPrivacyUsage
            handleGeotracking={handleGeotracking}
            handleCloseGeotracking={handleCloseGeotracking}
            buttonSubmitText={
              geotracking
                ? t('booking:confirm-private-usage')
                : t('booking:active-tracking')
            }
            open={modalTracking.isOpen}
            onClose={modalTracking.close}
          />
          <AskModal
            open={askCancelModal.isOpen}
            onClose={askCancelModal.close}
            onSuccess={handleCancelParticipation}
          />

          <ModifyBookingModal
            open={modifyModal.isOpen}
            onClose={modifyModal.close}
            rental={rental}
            onSuccess={handleChangeSuccess}
          />
          <CancelBookingModal
            open={cancelModal.isOpen}
            onClose={cancelModal.close}
            rentalId={id}
            onSuccess={handleCancelSuccess}
          />
        </div>
      ) : null}
    </div>
  );
};

export default withRouter(VehicleBody);
